import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Select,
  Text,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useDisclosure,
  useTheme,
  SlideFade,
  Skeleton,
  useBreakpointValue,
} from "@chakra-ui/react";
import api from '../auth/apiClient';
import { IconFiSearch, IconFiX } from "./Icons";


interface Inspeksi {
  id: number;
  tanggal: string;
  estate: string;
  divisi: string;
  foreman: string;
  mandor: string;
  blok: string;
  time: string;
  hasil_inspeksi: string;
}

interface FilterState {
  start_date?: string;
  end_date?: string;
  estate?: string;
  estate_id?: number;  // Tambahkan estate_id untuk menyimpan ID estate
  divisi?: string;
  company_id?: number;
  // Parameter baru
  category?: number;
  penalty?: number;
  pemanen?: string;
  users?: string;
  job_level_id?: number;
  q?: string;
  // time?: string; // Bisa dihapus jika sudah tidak digunakan
}

interface FilterProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
}

const FilterInspeksi = ({ filters, onFilterChange, onClearFilters, onApplyFilters }: FilterProps) => {
  const theme = useTheme();
  const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure({ defaultIsOpen: true });
  const [isFiltering, setIsFiltering] = useState(false);
  const [inspeksi, setInspeksi] = useState<Inspeksi[]>([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    companies: [] as { id: number, name: string }[],
    estates: [] as { id: number, name: string, company_id: number }[],
    divisis: [] as { id: number, name: string, estate_id: number }[],
    foremans: [] as string[],
    users: [] as string[],
    categories: [] as { id: number, name: string }[],
    penalty_codes: [] as { id: number, description: string, code: string }[],
    pemanens: [] as string[],
    bloks: [] as string[],
    job_levels: [] as { id: number, name: string }[],
  });
  const today = new Date();
  // const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
  const firstDayOfMonth = new Date(today);
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Initialize with default date range
  const [localFilters, setLocalFilters] = useState<FilterState>(() => {
    // Start with any existing filters
    const initialFilters = { ...filters };

    // Set default dates if none provided
    initialFilters.start_date ??= formatDateForInput(firstDayOfMonth);
    initialFilters.end_date ??= formatDateForInput(today);

    return initialFilters;
  });
  const [userInput, setUserInput] = useState(localFilters.users ?? "");
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [estateInput, setEstateInput] = useState(localFilters.estate ?? "");
  const [divisiInput, setDivisiInput] = useState(localFilters.divisi ?? "");
  const [pemanenInput, setPemanenInput] = useState(localFilters.pemanen ?? "");
  const [showPemanenSuggestions, setShowPemanenSuggestions] = useState(false);
  const footerGap = useBreakpointValue({ base: 2, md: 3 });


  const handleFilterChange = (field: keyof FilterState, value: string | number) => {
    setLocalFilters(prevFilters => ({
      ...prevFilters,
      [field]: value ?? undefined
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    onApplyFilters(); // This should trigger data fetching in parent
  };

  const handleClearFilters = () => {
    // Reset to default dates and clear other filters
    const resetFilters = {
      start_date: formatDateForInput(firstDayOfMonth),
      end_date: formatDateForInput(today),
    };

    // Update local state
    setLocalFilters(resetFilters);
    setUserInput("");
    setDivisiInput("");
    setPemanenInput("");

    // Notify parent
    onFilterChange(resetFilters);
    onClearFilters();
  };

  // Style constants
  const inputStyles = {
    size: "sm",
    borderRadius: "lg",
    borderColor: "gray.200",
    _hover: { borderColor: "gray.300" },
    _focus: {
      borderColor: theme.colors.blue[400],
      boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
    },
  };

  // List suggestion berdasarkan input
  const userSuggestions = userInput
    ? (options.users || []).filter((u) => u.toLowerCase().includes(userInput.toLowerCase()))
    : [];

  // Saat user pilih suggestion
  const handleUserSelect = (value: string) => {
    setUserInput(value);
    setLocalFilters(prev => ({ ...prev, users: value }));
    setShowUserSuggestions(false);
  };

  const pemanenSuggestions = pemanenInput
    ? (options.pemanens || []).filter((p) => p.toLowerCase().includes(pemanenInput.toLowerCase()))
    : [];

  const handlePemanenSelect = (value: string) => {
    setPemanenInput(value);
    setLocalFilters(prev => ({ ...prev, pemanen: value }));
    setShowPemanenSuggestions(false);
  };

  // to notify parent of default dates on component mount

  useEffect(() => {
    // Only notify parent of default dates if they weren't already set
    if (!filters.start_date || !filters.end_date) {
      // Apply default filters on mount
      const defaultFilters = {
        ...filters,
        start_date: localFilters.start_date,
        end_date: localFilters.end_date
      };

      // Notify parent component of default dates
      onFilterChange(defaultFilters);

      // Optional:trigger data loading immediately
      // onApplyFilters(); 
    }
  }, []);  // Empty dependency array ensures this runs once on mount

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch estates from the correct endpoint
        const estatesResponse = await api.get('/inspeksi/web/estate-db');
        const optionsResponse = await api.get('/inspeksi/options');

        // Combine the responses
        setOptions({
          ...optionsResponse.data,
          estates: estatesResponse.data
        });

      } catch (error) {
        console.error("Error fetching options", error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    //clear estate filter when company changes
    if (localFilters.company_id !== filters.company_id) {
      setEstateInput("");
      setLocalFilters(prev => ({ ...prev, estate: undefined, estate_id: undefined, divisi: undefined }));
      setDivisiInput("");
    }
  }, [localFilters.company_id, filters.company_id]);

  useEffect(() => {
    // Clear divisi filter when estate changes
    if (localFilters.estate !== filters.estate) {
      setDivisiInput("");
      setLocalFilters(prev => ({ ...prev, divisi: undefined }));
    }
  }, [localFilters.estate, filters.estate]);

  useEffect(() => {
    console.log("Window width:", window.innerWidth);
  }, []);


  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      bg="white"
    >
      {/* Header Section - Fixed */}
      <Flex
        as="header"
        position="sticky"
        top={0}
        bg="white"
        zIndex={2}
        px={useBreakpointValue({ base: 3, md: 4 })}
        py={useBreakpointValue({ base: 2, md: 0 })}
        align="center"
        minHeight={useBreakpointValue({ base: "60px", md: "72px" })}
      >
        <Heading size={useBreakpointValue({ base: "md", md: "md" })}>
          Histori Inspeksi
        </Heading>

      </Flex>

      {/* Scrollable Content */}
      <Box
        flex={1}
        overflowY="auto"
        position="relative"
      >
        <SlideFade in={isAdvancedOpen}>
          <Grid templateColumns="1fr" gap={4} p="5"> {/* Tambahkan padding bottom untuk footer */}
            <Flex gap={4}>
              <Box flex={1} mb={2}>
                <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                  Dari Tanggal
                </Text>
                <Input
                  type="date"
                  value={localFilters.start_date ?? ""}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                  placeholder="YYYY-MM-DD"
                  title="Dari tanggal (format: YYYY-MM-DD)"
                  {...inputStyles}
                  width="full"
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                  Sampai Tanggal
                </Text>
                <Input
                  type="date"
                  value={localFilters.end_date ?? ""}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                  {...inputStyles}
                  width="full"
                />
              </Box>
            </Flex>

            {/* Semua konten filter */}
            <Box width="full" position="relative">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Cari berdasarkan blok atau deskripsi
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <IconFiSearch />
                </InputLeftElement>
                <Input
                  placeholder="Cari berdasarkan blok atau deskripsi"
                  value={localFilters.q ?? ""}
                  onChange={(e) => handleFilterChange("q", e.target.value)}
                  {...inputStyles}
                  pr="40px"
                />
                {localFilters.q && (
                  <InputRightElement width="40px">
                    <IconButton
                      aria-label="Clear search"
                      icon={<IconFiX />}
                      size="xs"
                      variant="ghost"
                      onClick={() => handleFilterChange("q", "")}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
            <Box width="full" position="relative">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Pemeriksa
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <IconFiSearch />
                </InputLeftElement>
                <Input
                  value={userInput}
                  placeholder="Cari pemeriksa..."
                  onChange={(e) => {
                    setUserInput(e.target.value);
                    setLocalFilters(prev => ({ ...prev, users: e.target.value }));
                    setShowUserSuggestions(true);
                  }}
                  onFocus={() => setShowUserSuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowUserSuggestions(false), 100);
                  }}
                  {...inputStyles}
                  width="full"
                />
                {userInput && (
                  <InputRightElement width="40px">
                    <IconButton
                      aria-label="Clear search"
                      icon={<IconFiX />}
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setUserInput("");
                        setLocalFilters(prev => ({ ...prev, users: "" }));
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              {showUserSuggestions && userSuggestions.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="sm"
                  maxH="200px"
                  overflowY="auto"
                  zIndex={20}
                >
                  {userSuggestions.map((user) => (
                    <Box
                      key={user}
                      px={3}
                      py={2}
                      _hover={{ bg: "gray.100" }}
                      cursor="pointer"
                      onMouseDown={() => handleUserSelect(user)}
                    >
                      {user}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Company
              </Text>
              <Select
                value={localFilters.company_id ?? ""}
                onChange={(e) => handleFilterChange("company_id", Number(e.target.value))}
                {...inputStyles}
                width="full"
              >
                <option value="">Semua Company</option>
                {options.companies?.map((comp) => (
                  <option key={comp.id} value={comp.id}>
                    {comp.name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Estate
              </Text>
              <Select
                value={estateInput}
                onChange={(e) => {
                  const selectedEstate = options.estates.find(est => est.name === e.target.value);
                  setEstateInput(e.target.value);
                  setLocalFilters(prev => ({
                    ...prev,
                    estate: e.target.value,
                    estate_id: selectedEstate ? selectedEstate.id : undefined,
                    divisi: undefined // Clear divisi when estate changes
                  }));
                }}
                placeholder="Semua Estate"
                {...inputStyles}
                width="full"
                isDisabled={!localFilters.company_id}
              >
                {options.estates
                  ?.filter(est => !localFilters.company_id || est.company_id === localFilters.company_id)
                  .map((estate) => (
                    <option key={estate.id} value={estate.name}>
                      {estate.name}
                    </option>
                  ))}
              </Select>
            </Box>
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Divisi
              </Text>
              <Select
                value={divisiInput}
                onChange={(e) => {
                  setDivisiInput(e.target.value);
                  setLocalFilters(prev => ({ ...prev, divisi: e.target.value }));
                }}
                placeholder="Semua Divisi"
                {...inputStyles}
                width="full"
                isDisabled={!localFilters.estate}
              >
                {options.divisis
                  ?.filter(d => !localFilters.estate_id || d.estate_id === localFilters.estate_id)
                  .map((divisi) => (
                    <option key={divisi.id} value={divisi.name}>
                      {divisi.name}
                    </option>
                  ))}
              </Select>
            </Box>
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Job Level
              </Text>
              <Select
                value={localFilters.job_level_id ?? ""}
                onChange={(e) => handleFilterChange("job_level_id", e.target.value)}
                {...inputStyles}
                width="full"
              >
                <option value="">Semua Job Level</option>
                {options.job_levels?.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Kategori
              </Text>
              <Select
                value={localFilters.category ?? ""}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                {...inputStyles}
                width="full"
              >
                <option value="">Semua Kategori</option>
                {options.categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </Box>
            {/* Tambahkan filter penalty baru */}
            <Box width="full">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Jenis Penalti
              </Text>
              <Select
                value={localFilters.penalty ?? ""}
                onChange={(e) => handleFilterChange("penalty", Number(e.target.value))}
                {...inputStyles}
                width="full"
              >
                <option value="">Semua Penalti</option>
                {options.penalty_codes?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} - {p.description}
                  </option>
                ))}
              </Select>
            </Box>
            {/* Filter Pemanen - Suggestion Search */}
            <Box width="full" position="relative">
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Pemanen
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <IconFiSearch />
                </InputLeftElement>
                <Input
                  value={pemanenInput}
                  placeholder="Cari pemanen..."
                  onChange={(e) => {
                    setPemanenInput(e.target.value);
                    setLocalFilters(prev => ({ ...prev, pemanen: e.target.value }));
                    setShowPemanenSuggestions(true);
                  }}
                  onFocus={() => setShowPemanenSuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowPemanenSuggestions(false), 100);
                  }}
                  {...inputStyles}
                  width="full"
                />
                {pemanenInput && (
                  <InputRightElement width="40px">
                    <IconButton
                      aria-label="Clear search"
                      icon={<IconFiX />}
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setPemanenInput("");
                        setLocalFilters(prev => ({ ...prev, pemanen: "" }));
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              {showPemanenSuggestions && pemanenSuggestions.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="sm"
                  maxH="200px"
                  overflowY="auto"
                  zIndex={20}
                >
                  {pemanenSuggestions.map((pemanen) => (
                    <Box
                      key={pemanen}
                      px={3}
                      py={2}
                      _hover={{ bg: "gray.100" }}
                      cursor="pointer"
                      onMouseDown={() => handlePemanenSelect(pemanen)}
                    >
                      {pemanen}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </SlideFade>
      </Box>
      {/* Fixed Footer */}
      <Flex
        position="sticky"
        bg="white"
        zIndex={3}
        padding="16px"
        justify="flex-end"
        gap={footerGap}
        minHeight="240px"
      >
        <Button
          variant="outline"
          colorScheme="red"
          size="sm"
          onClick={handleClearFilters}
        >
          Hapus Filter
        </Button>

        <Button
          colorScheme="green"
          size="sm"
          onClick={handleApplyFilters}
          leftIcon={<IconFiSearch />}
        >
          Terapkan Filter
        </Button>
      </Flex>
      {/* </Box> */}
      {/* Loading State */}
      {loading && (
        <Box p={4}>
          <Skeleton height="40px" mb={2} borderRadius="md" />
          <Skeleton height="40px" mb={2} borderRadius="md" />
          <Skeleton height="40px" borderRadius="md" />
        </Box>
      )}
    </Box>
  );
};

export default FilterInspeksi;
